import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
  Switch,
  message,
} from 'antd';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../../services/api';
import type { MerchantOption } from '../../types/shopPromotion';
import type { ShopPromotionType } from '../../types/shopPromotion';
import { parsePromotionMetadata } from '../../utils/shopPromotionLabels';
import type { ShopPromotionRecord } from '../../types/shopPromotion';

const { RangePicker } = DatePicker;

interface MenuDishOption {
  value: number;
  label: string;
}

export interface ShopPromotionFormProps {
  mode: 'create' | 'edit';
  initial?: ShopPromotionRecord | null;
  onSubmit: (body: Record<string, unknown>) => Promise<void>;
  onCancel: () => void;
  submitLabel?: string;
}

export const ShopPromotionForm: React.FC<ShopPromotionFormProps> = ({
  mode,
  initial,
  onSubmit,
  onCancel,
  submitLabel,
}) => {
  const [form] = Form.useForm();
  const [merchants, setMerchants] = useState<MerchantOption[]>([]);
  const [dishes, setDishes] = useState<MenuDishOption[]>([]);
  const [loadingMenu, setLoadingMenu] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const watchedType = Form.useWatch('discount_type', form) as
    | ShopPromotionType
    | undefined;
  const promotionType = watchedType ?? 'percentage';

  const loadMerchants = useCallback(async () => {
    try {
      const res = await api.get<{
        success: boolean;
        data: Array<{
          id: number;
          name: string;
          restaurantId?: number;
          restaurantName?: string;
        }>;
      }>('/merchants', { params: { page: 1, limit: 500 } });
      if (!res.data.success || !Array.isArray(res.data.data)) return;
      const opts: MerchantOption[] = res.data.data
        .map(m => {
          const restaurantId = m.restaurantId ?? m.id;
          if (!restaurantId) return null;
          return {
            merchantId: m.id,
            restaurantId,
            label: m.restaurantName
              ? `${m.restaurantName} (#${restaurantId})`
              : `${m.name} (#${restaurantId})`,
          };
        })
        .filter((x): x is MerchantOption => x != null);
      setMerchants(opts);
    } catch {
      message.error('Không tải được danh sách quán');
    }
  }, []);

  const loadMenu = useCallback(async (merchantId: number) => {
    setLoadingMenu(true);
    try {
      const res = await api.get<{
        success: boolean;
        data: { dishes: Array<{ id: string; name: string }> };
      }>(`/merchants/${merchantId}/menu`);
      const dishList = res.data?.data?.dishes ?? [];
      setDishes(
        dishList.map(d => ({
          value: parseInt(d.id, 10),
          label: d.name,
        }))
      );
    } catch {
      setDishes([]);
      message.warning('Không tải được thực đơn quán');
    } finally {
      setLoadingMenu(false);
    }
  }, []);

  useEffect(() => {
    loadMerchants();
  }, [loadMerchants]);

  useEffect(() => {
    if (!initial) {
      form.setFieldsValue({
        discount_type: 'percentage',
        is_active: true,
        is_public: true,
        max_usage_per_user: 1,
        max_total_usage: 100,
        minimum_order_amount: 0,
        valid_range: [dayjs(), dayjs().add(30, 'day')],
      });
      return;
    }

    const meta = parsePromotionMetadata(initial.metadata);
    const restaurantId = initial.restaurant_id;
    const merchant = merchants.find(m => m.restaurantId === restaurantId);
    const buyItems = (meta.buy_items ?? meta.buyItems) as
      | Array<Record<string, unknown>>
      | undefined;
    const giftItems = (meta.gift_items ?? meta.giftItems) as
      | Array<Record<string, unknown>>
      | undefined;
    const comboItems = (meta.combo_items ?? meta.comboItems) as
      | Array<Record<string, unknown>>
      | undefined;

    form.setFieldsValue({
      restaurant_id: restaurantId,
      merchant_id: merchant?.merchantId,
      code: initial.code,
      name: initial.name,
      description: initial.description,
      discount_type: initial.discount_type,
      discount_value: initial.discount_value,
      minimum_order_amount: initial.minimum_order_amount ?? 0,
      max_discount_amount: initial.max_discount_amount,
      max_usage_per_user: 1,
      max_total_usage: initial.max_total_usage ?? 100,
      is_active: initial.is_active,
      is_public: initial.is_public ?? true,
      valid_range: [dayjs(initial.valid_from), dayjs(initial.valid_until)],
      combo_price:
        Number(meta.combo_price ?? meta.comboPrice) || initial.discount_value,
      max_combos_per_order: meta.max_combos_per_order ?? meta.maxCombosPerOrder,
      buy_items: (buyItems ?? []).map(row => ({
        food_item_id: row.food_item_id ?? row.foodItemId,
        quantity: row.quantity ?? 1,
      })),
      gift_items: (giftItems ?? []).map(row => ({
        food_item_id: row.food_item_id ?? row.foodItemId,
        quantity: row.quantity ?? 1,
      })),
      combo_items: (comboItems ?? []).map(row => ({
        food_item_id: row.food_item_id ?? row.foodItemId,
        quantity: row.quantity ?? 1,
      })),
    });
    if (merchant?.merchantId) {
      loadMenu(merchant.merchantId);
    }
  }, [initial, merchants, form, loadMenu]);

  const onRestaurantChange = (restaurantId: number) => {
    const merchant = merchants.find(m => m.restaurantId === restaurantId);
    if (merchant) {
      form.setFieldValue('merchant_id', merchant.merchantId);
      loadMenu(merchant.merchantId);
    }
  };

  const buildBody = (
    values: Record<string, unknown>
  ): Record<string, unknown> => {
    const range = values.valid_range as [dayjs.Dayjs, dayjs.Dayjs];
    const discountType = values.discount_type as ShopPromotionType;
    const restaurantId = Number(values.restaurant_id);

    let metadata: Record<string, unknown> | undefined;
    let discountValue = Number(values.discount_value) || 0;

    if (discountType === 'bxgy') {
      metadata = {
        promotion_kind: 'bxgy',
        buy_items: (values.buy_items as Array<Record<string, unknown>>) ?? [],
        gift_items: (values.gift_items as Array<Record<string, unknown>>) ?? [],
      };
      discountValue = 0;
    } else if (discountType === 'combo') {
      const comboPrice = Number(values.combo_price) || 0;
      metadata = {
        promotion_kind: 'combo',
        combo_price: comboPrice,
        combo_items:
          (values.combo_items as Array<Record<string, unknown>>) ?? [],
      };
      const maxCombos = values.max_combos_per_order;
      if (maxCombos != null && maxCombos !== '') {
        metadata.max_combos_per_order = Number(maxCombos);
      }
      discountValue = comboPrice;
    }

    const body: Record<string, unknown> = {
      code: String(values.code || '')
        .trim()
        .toUpperCase(),
      name: String(values.name || '').trim(),
      description: values.description
        ? String(values.description).trim()
        : undefined,
      restaurant_id: restaurantId,
      source_type: 'MERCHANT',
      discount_type: discountType,
      discount_value: discountValue,
      minimum_order_amount: Number(values.minimum_order_amount) || 0,
      max_usage_per_user: Number(values.max_usage_per_user) || 1,
      max_total_usage: Number(values.max_total_usage) || 100,
      valid_from: range[0].toISOString(),
      valid_until: range[1].toISOString(),
      is_active: values.is_active !== false,
      is_public: values.is_public !== false,
    };

    if (
      values.max_discount_amount != null &&
      values.max_discount_amount !== ''
    ) {
      body.max_discount_amount = Number(values.max_discount_amount);
    }
    if (metadata) body.metadata = metadata;
    return body;
  };

  const handleFinish = async (values: Record<string, unknown>) => {
    try {
      setSubmitting(true);
      await onSubmit(buildBody(values));
    } finally {
      setSubmitting(false);
    }
  };

  const foodLineFields = (name: string, label: string) => (
    <Form.List name={name}>
      {(fields, { add, remove }) => (
        <>
          <div style={{ marginBottom: 8, fontWeight: 600 }}>{label}</div>
          {fields.map(field => (
            <Space
              key={field.key}
              align="baseline"
              style={{ display: 'flex', marginBottom: 8 }}
            >
              <Form.Item
                {...field}
                name={[field.name, 'food_item_id']}
                rules={[{ required: true, message: 'Chọn món' }]}
              >
                <Select
                  showSearch
                  optionFilterProp="label"
                  placeholder="Món"
                  options={dishes}
                  loading={loadingMenu}
                  style={{ width: 220 }}
                />
              </Form.Item>
              <Form.Item
                {...field}
                name={[field.name, 'quantity']}
                rules={[{ required: true, message: 'SL' }]}
                initialValue={1}
              >
                <InputNumber min={1} placeholder="SL" />
              </Form.Item>
              <MinusCircleOutlined onClick={() => remove(field.name)} />
            </Space>
          ))}
          <Button type="dashed" onClick={() => add()} icon={<PlusOutlined />}>
            Thêm món
          </Button>
        </>
      )}
    </Form.List>
  );

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleFinish}
      disabled={mode === 'edit' && !initial}
    >
      <Form.Item
        name="restaurant_id"
        label="Quán (shop)"
        rules={[{ required: true, message: 'Chọn quán' }]}
      >
        <Select
          showSearch
          optionFilterProp="label"
          placeholder="Chọn quán"
          options={merchants.map(m => ({
            value: m.restaurantId,
            label: m.label,
          }))}
          onChange={onRestaurantChange}
          disabled={mode === 'edit'}
        />
      </Form.Item>
      <Form.Item name="merchant_id" hidden>
        <Input />
      </Form.Item>

      <Form.Item
        name="discount_type"
        label="Loại khuyến mãi"
        rules={[{ required: true }]}
      >
        <Select
          options={[
            { value: 'percentage', label: 'Giảm %' },
            { value: 'fixed_amount', label: 'Giảm cố định' },
            { value: 'bxgy', label: 'Mua tặng (BXGY)' },
            { value: 'combo', label: 'Combo món' },
          ]}
          disabled={mode === 'edit'}
        />
      </Form.Item>

      <Form.Item
        name="code"
        label="Mã KM"
        rules={[{ required: true, message: 'Nhập mã' }]}
      >
        <Input disabled={mode === 'edit'} />
      </Form.Item>
      <Form.Item
        name="name"
        label="Tên chiến dịch"
        rules={[{ required: true, message: 'Nhập tên' }]}
      >
        <Input />
      </Form.Item>
      <Form.Item name="description" label="Mô tả">
        <Input.TextArea rows={2} />
      </Form.Item>

      {(promotionType === 'percentage' || promotionType === 'fixed_amount') && (
        <>
          <Form.Item
            name="discount_value"
            label={promotionType === 'percentage' ? 'Giảm (%)' : 'Giảm (VND)'}
            rules={[{ required: true, message: 'Nhập giá trị' }]}
          >
            <InputNumber min={0.01} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="minimum_order_amount" label="Đơn tối thiểu (VND)">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          {promotionType === 'percentage' && (
            <Form.Item name="max_discount_amount" label="Giảm tối đa (VND)">
              <InputNumber min={0} style={{ width: '100%' }} />
            </Form.Item>
          )}
        </>
      )}

      {promotionType === 'bxgy' && (
        <>
          {foodLineFields('buy_items', 'Món mua')}
          <div style={{ height: 16 }} />
          {foodLineFields('gift_items', 'Món tặng')}
        </>
      )}

      {promotionType === 'combo' && (
        <>
          <Form.Item
            name="combo_price"
            label="Giá combo (VND)"
            rules={[{ required: true, message: 'Nhập giá combo' }]}
          >
            <InputNumber min={1} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="max_combos_per_order" label="Tối đa combo / đơn">
            <InputNumber min={1} style={{ width: '100%' }} />
          </Form.Item>
          {foodLineFields('combo_items', 'Món trong combo (≥ 2)')}
        </>
      )}

      <Form.Item
        name="valid_range"
        label="Thời gian áp dụng"
        rules={[{ required: true, message: 'Chọn thời gian' }]}
      >
        <RangePicker
          showTime
          format="DD/MM/YYYY HH:mm"
          style={{ width: '100%' }}
        />
      </Form.Item>

      <Form.Item name="max_total_usage" label="Lượt dùng tối đa">
        <InputNumber min={1} style={{ width: '100%' }} />
      </Form.Item>

      <Form.Item name="is_active" label="Đang bật" valuePropName="checked">
        <Switch />
      </Form.Item>
      <Form.Item
        name="is_public"
        label="Hiển thị công khai"
        valuePropName="checked"
      >
        <Switch />
      </Form.Item>

      <Space>
        <Button type="primary" htmlType="submit" loading={submitting}>
          {submitLabel ??
            (mode === 'create' ? 'Tạo khuyến mãi' : 'Lưu thay đổi')}
        </Button>
        <Button onClick={onCancel}>Hủy</Button>
      </Space>
    </Form>
  );
};
